<template>
  <div class="form-group" :class="{ 
    'has-warning': !hasValidation, 
    'has-success': hasValidation, 
    'opacity50': isDisabled 
  }">
    <div class="input-group-wrapper">
      <label v-if="label && label !== ''" :for="elemId" class="input-label" style="white-space:nowrap;">
        <i v-if="isRequired" class="pi pi-exclamation-triangle text-warning" v-show="!hasValidation"></i>
        <i v-if="isRequired" class="pi pi-check text-success" v-show="hasValidation"></i>
        <span v-if="isRequired">*</span>{{ label }}
      </label>
      <input
        type="text"
        inputmode="numeric"
        autocomplete="off"
        :id="elemId"
        :data-testid="elemId ? `${elemId}-currency-input` : 'currency-input'"
        :value="displayValue"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        :placeholder="placeholder"
        :disabled="isDisabled"
        class="p-inputtext p-component"
        :class="{ 
          'p-invalid': !hasValidation && isRequired,
          'p-disabled': isDisabled,
          'currency-input': true
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { permissionsState } from '../services/permissionsService';
import { currencyMinorDigits } from '../utils/currencyMinorUnits';

const props = defineProps({
  elemId: String,
  currencyPrefix: {
    type: String,
    default: null // Will use permissionsState.normalizedLocale if not provided
  },
  currency: {
    type: String,
    default: null // Allow explicit currency override
  },
  placeholder: {
    type: String,
    default: '0,00'
  },
  label: String,
  validation: {
    type: Function as any,
    default: () => true
  },
  ngRequired: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  modelValue: Number,
  value: Number // Keep for backward compatibility
});

const emit = defineEmits<{
  'update:modelValue': [value: number];
  'input': [value: number];
}>();

// Reactive data
const internalValue = ref<number>(props.modelValue ?? props.value ?? 0);
const displayValue = ref<string>('');
// Accumulated value in MINOR units (integer). For 2-decimal currencies (BRL/USD)
// one minor unit is a centavo/cent; for 0-decimal currencies (CLP) one minor
// unit is a whole peso. The major <-> minor conversion uses `factor` below.
const minorValue = ref<number>(0);

// Computed properties
const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  (props.validation as () => boolean)()
);

// Map locale prefix to locale and currency code
const localeCode = computed((): string => {
  // Use explicit prop, or fall back to user's locale from permissions, or default to pt-BR
  return props.currencyPrefix || (permissionsState as any)?.normalizedLocale || 'pt-BR';
});

const currencyCode = computed((): string => {
  // Allow explicit currency override
  if (props.currency) {
    return props.currency;
  }

  // Map common locales to currency codes
  const localeToCurrency: Record<string, string> = {
    'pt-BR': 'BRL',
    'en-US': 'USD',
    'de-DE': 'EUR',
    'en-IN': 'INR',
    'ja-JP': 'JPY',
    'es-ES': 'EUR',
    'fr-FR': 'EUR',
    'it-IT': 'EUR',
    'en-GB': 'GBP',
    'zh-CN': 'CNY',
    'es-AR': 'ARS',
    'es-MX': 'MXN',
    'es-CL': 'CLP',
    'es-EC': 'USD',
  };

  // Use the computed locale (which includes user's locale from permissions)
  return localeToCurrency[localeCode.value] || 'BRL';
});

// Minor-unit digit count for the active currency (BRL/USD -> 2, CLP -> 0),
// derived from the platform Intl data so display matches the i18n n() renderer.
const minorDigits = computed((): number =>
  currencyMinorDigits(localeCode.value, currencyCode.value)
);

// Integer factor between major and minor units (CLP -> 1, BRL/USD -> 100).
const factor = computed((): number => 10 ** minorDigits.value);

// Format a minor-unit integer to the currency display string. Lets Intl apply
// the currency's own minorUnit (no hardcoded 2 decimals), so CLP renders with
// zero decimals and BRL/USD with two — parity with n(value,'currency').
const formatCurrency = (minor: number): string => {
  const value = minor / factor.value;

  try {
    return new Intl.NumberFormat(localeCode.value, {
      style: 'currency',
      currency: currencyCode.value
    }).format(value);
  } catch (e) {
    // Fallback formatting if locale/currency combination is invalid
    return `${currencyCode.value} ${value.toFixed(minorDigits.value)}`;
  }
};

// Update display value based on the accumulated minor-unit value
const updateDisplay = (): void => {
  displayValue.value = formatCurrency(minorValue.value);
};

// Methods
//
// Digit handling happens on the INPUT event, never on keydown/keyCode:
// Android soft keyboards (GBoard etc.) fire keydown with keyCode 229 for
// every key (IME composition), so any keyCode-based filter blocks typing
// entirely on Android — the exact bug users reported on the recebimento
// modal. The input event carries the resulting text regardless of keyboard,
// so we re-derive the minor-unit accumulator from the digits present in the
// field after each change. Deleting a formatting char (e.g. the comma)
// leaves the digits intact, so the value is unchanged and the display just
// re-formats. Paste, autofill and numpads all land here too.
const handleInput = (event: Event): void => {
  const el = event.target as HTMLInputElement;
  const digits = (el.value ?? '').replace(/\D/g, '');

  // Empty field (select-all + delete) resets to zero.
  let next = digits === '' ? 0 : parseInt(digits, 10);

  // Limit to a reasonable max value (999,999,999.99)
  if (!Number.isFinite(next) || next > 99999999999) {
    next = Math.min(next || 0, 99999999999);
  }

  minorValue.value = next;
  updateDisplay();
  // :value binding won't touch the DOM when displayValue is unchanged
  // (e.g. user deleted only the currency symbol) — force the element back
  // to the canonical formatted string and keep the caret at the end.
  el.value = displayValue.value;
  el.setSelectionRange(el.value.length, el.value.length);
  emitValue();
};

const emitValue = (): void => {
  // Emit a MAJOR-unit number (reais/pesos); callers apply the *factor at their
  // own save boundary. For CLP factor=1 so the emitted value is whole pesos.
  const value = minorValue.value / factor.value;
  internalValue.value = value;
  emit('update:modelValue', value);
  emit('input', value);
};

const handleFocus = (event: FocusEvent): void => {
  // Select all on focus for easy replacement
  (event.target as HTMLInputElement).select();
};

const handleBlur = (): void => {
  // Ensure display is formatted correctly on blur
  updateDisplay();
};

// Initialize from props. `val` is a MAJOR-unit number; store it as minor units.
const initializeValue = (val: number | null | undefined): void => {
  if (val !== null && val !== undefined) {
    minorValue.value = Math.round(val * factor.value);
  } else {
    minorValue.value = 0;
  }
  updateDisplay();
};

// Watchers
watch(() => props.modelValue, (newVal: number | undefined) => {
  if (newVal !== internalValue.value) {
    initializeValue(newVal);
  }
});

watch(() => props.value, (newVal: number | undefined) => {
  if (newVal !== internalValue.value) {
    initializeValue(newVal);
  }
});

// Watch locale/currency changes: the minor-unit factor may change (e.g. BRL->CLP),
// so re-derive the minor accumulator from the current major-unit value and
// re-render with the new currency's decimals.
watch([localeCode, currencyCode], () => {
  initializeValue(internalValue.value);
});

// Initialize on mount
onMounted(() => {
  initializeValue(props.modelValue ?? props.value ?? 0);
});
</script>

<style scoped>
.opacity50 {
  opacity: 0.5;
}

.input-group-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.input-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.text-warning {
  color: #f39c12;
}

.text-success {
  color: #27ae60;
}

.currency-input {
  width: 100%;
  font-weight: bold;
  text-align: right;
}

.currency-input:focus {
  outline: none;
}
</style>
